import React, { useRef, useState, useEffect } from 'react';
import PressableScale from './motion/PressableScale';
import {
  View,
  TextInput,
  TouchableOpacity,
  Platform,
  StyleSheet,
  Animated,
  Easing,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ThemeGradientOverlay, ThemeGlassBackground } from './ThemeGradients';
import { useTheme } from '../context/ThemeContext';
import { useNavigation } from '../context/NavigationContext';

import { isMobile, isTablet, SCREEN_WIDTH } from '../utils/responsive';
const isVerySmall = SCREEN_WIDTH < 500;

const responsive = (verySmall, mobile, tablet, desktop) => {
  if (isVerySmall) return verySmall;
  if (isMobile) return mobile;
  if (isTablet) return tablet;
  return desktop;
};

const PLACEHOLDER_SAMPLES = [
  'best nba sgp tonight',
  'lakers -3.5 and lebron 9.5+ ast',
  'under 220.5 and jokic 10+ reb',
  'same game parlay warriors today',
  'nfl sgp chiefs tomorrow',
];

/**
 * Shared animated search input used on both the Home page and the Results page.
 *
 * Props:
 *  onNavigate       – (screenName, options) navigation helper (passed through from HomeScreen)
 *  valueProp        – controlled value (optional)
 *  onValueChange    – callback when value changes
 *  onSearchStart    – called with query string just before navigation / execution
 *  onExecuteSearch  – if provided, called instead of navigating to SgpFlow.
 *                     Use this on the results page to re-run the search in-place.
 *  fieldResetKey    – increment to clear the field
 */
const SearchInput = ({
  onNavigate,
  valueProp,
  onValueChange,
  onSearchStart,
  onExecuteSearch,
  fieldResetKey = 0,
  style,
}) => {
  const [isFocused, setFocused] = useState(false);
  const [value, setValue] = useState(valueProp || '');
  const { photoUploadEnabled, navigateTo: navTo } = useNavigation();
  const focusT = useRef(new Animated.Value(0)).current;
  const borderRadiusT = useRef(new Animated.Value(10)).current;
  const { isGradientEnabled, enableGradient, disableGradient } = useTheme();
  const [typedPlaceholder, setTypedPlaceholder] = useState('');
  const placeholderIdxRef = useRef(0);
  const typeIdxRef = useRef(0);
  const directionRef = useRef(1); // 1 = typing, -1 = deleting
  const timerRef = useRef(null);

  const onFocus = () => {
    setFocused(true);
    enableGradient('searchInput');
    focusT.stopAnimation();
    borderRadiusT.stopAnimation();
    const currentFocus = focusT._value || 0;
    const remainingDuration = Math.max(2000 * (1 - currentFocus), 500);
    Animated.parallel([
      Animated.timing(focusT, {
        toValue: 1,
        duration: remainingDuration,
        easing: Easing.bezier(0.22, 1, 0.36, 1),
        useNativeDriver: false,
      }),
      Animated.timing(borderRadiusT, {
        toValue: 999,
        duration: remainingDuration,
        easing: Easing.bezier(0.22, 1, 0.36, 1),
        useNativeDriver: false,
      }),
    ]).start();
  };

  const onBlur = () => {
    setFocused(false);
    focusT.stopAnimation();
    borderRadiusT.stopAnimation();
    const currentFocus = focusT._value || 0;
    const remainingDuration = 2000 * currentFocus;
    Animated.parallel([
      Animated.timing(focusT, {
        toValue: 0,
        duration: remainingDuration,
        easing: Easing.bezier(0.22, 1, 0.36, 1),
        useNativeDriver: false,
      }),
      Animated.timing(borderRadiusT, {
        toValue: 10,
        duration: remainingDuration,
        easing: Easing.bezier(0.22, 1, 0.36, 1),
        useNativeDriver: false,
      }),
    ]).start(() => {
      setTimeout(() => disableGradient('searchInput'), 50);
    });
  };

  const handleSubmit = () => {
    const q = (valueProp !== undefined ? valueProp : value).trim();
    if (!q) return;
    try {
      if (Platform.OS === 'web') window.sessionStorage.setItem('sgp:lastQuery', q);
    } catch {}
    if (onSearchStart) onSearchStart(q);
    if (onExecuteSearch) {
      onExecuteSearch(q);
    } else {
      onNavigate('SgpFlow', { payload: { query: q } });
    }
  };

  useEffect(() => {
    if (valueProp !== undefined && valueProp !== value) setValue(valueProp);
  }, [valueProp]);

  useEffect(() => {
    if (fieldResetKey > 0) {
      setValue('');
      onValueChange?.('');
    }
  }, [fieldResetKey]);

  // Typewriter placeholder when not focused and empty
  useEffect(() => {
    const shouldType = !isFocused && !(valueProp !== undefined ? valueProp : value);
    if (!shouldType) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setTypedPlaceholder('');
      return;
    }
    if (timerRef.current) return;
    timerRef.current = setInterval(() => {
      const curSample = PLACEHOLDER_SAMPLES[placeholderIdxRef.current % PLACEHOLDER_SAMPLES.length];
      const i = typeIdxRef.current + directionRef.current;
      if (i <= 0) {
        directionRef.current = 1;
        placeholderIdxRef.current = (placeholderIdxRef.current + 1) % PLACEHOLDER_SAMPLES.length;
        typeIdxRef.current = 0;
        setTypedPlaceholder('');
      } else if (i >= curSample.length) {
        typeIdxRef.current = curSample.length;
        setTypedPlaceholder(curSample);
        directionRef.current = 0;
        setTimeout(() => { directionRef.current = -1; }, 2000);
      } else {
        typeIdxRef.current = i;
        setTypedPlaceholder(curSample.slice(0, Math.min(i, curSample.length)));
      }
    }, 70);
    return () => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    };
  }, [isFocused, valueProp, value]);

  return (
    <View style={[styles.searchContainer, style]}>
      <Animated.View style={styles.searchAnimatedWrap}>
        <Animated.View style={{ borderRadius: borderRadiusT, overflow: 'hidden' }}>
          <ThemeGlassBackground
            opacity={focusT}
            borderRadius={borderRadiusT}
            enabled={isGradientEnabled('searchInput')}
          />
          <ThemeGradientOverlay
            opacity={focusT}
            borderRadius={borderRadiusT}
            enabled={isGradientEnabled('searchInput')}
          />
        </Animated.View>
        <Animated.View
          style={[
            styles.searchInput,
            {
              borderRadius: borderRadiusT,
              backgroundColor: 'rgba(0,0,0,0.3)',
              backdropFilter: 'blur(10px)',
            },
          ]}
        >
          <TextInput
            placeholder={isFocused || value ? '' : typedPlaceholder || 'Type a bet idea…'}
            placeholderTextColor="rgba(255, 255, 255, 0.5)"
            style={styles.searchText}
            value={valueProp !== undefined ? valueProp : value}
            onChangeText={(t) => { setValue(t); onValueChange && onValueChange(t); }}
            onFocus={onFocus}
            onBlur={onBlur}
            onSubmitEditing={handleSubmit}
            returnKeyType="search"
            selectionColor={Platform.OS === 'web' ? undefined : 'rgba(255,255,255,0.9)'}
          />
          <View style={styles.iconsContainer}>
            <PressableScale
              onPress={() => navTo('Docs')}
              style={{ marginRight: photoUploadEnabled ? 8 : 0 }}
            >
              <Ionicons name="information-circle-outline" size={26} color="rgba(255,255,255,0.55)" />
            </PressableScale>
            {/* Search button — always visible so users can tap it on the results page */}
            <PressableScale
              onPress={handleSubmit}
              style={styles.searchButton}
            >
              <Ionicons name="search" size={20} color="rgba(255,255,255,0.8)" />
            </PressableScale>
          </View>
        </Animated.View>
      </Animated.View>
    </View>
  );
};

const styles = StyleSheet.create({
  searchContainer: {
    width: '100%',
    alignSelf: 'stretch',
    paddingVertical: responsive(12, 16, 24, 32),
    alignItems: 'stretch',
    gap: responsive(8, 8, 10, 12),
  },
  searchAnimatedWrap: {
    width: '100%',
    alignSelf: 'stretch',
  },
  searchInput: {
    width: '100%',
    height: responsive(48, 52, 56, 60),
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: responsive(12, 14, 17, 20),
    paddingVertical: responsive(8, 10, 12, 14),
    borderRadius: responsive(10, 12, 14, 16),
    borderWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.25)',
    borderLeftColor: 'rgba(255,255,255,0.18)',
    borderRightColor: 'rgba(0,0,0,0.35)',
    borderBottomColor: 'rgba(0,0,0,0.45)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    shadowColor: 'rgba(0, 0, 0, 0.25)',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 4,
  },
  searchText: {
    flex: 1,
    fontFamily: 'Slate',
    fontSize: responsive(13, 14, 16, Platform.OS === 'web' ? 18 : 15),
    fontWeight: '600',
    lineHeight: responsive(16, 18, 20, Platform.OS === 'web' ? 22 : 20),
    color: 'rgba(255, 255, 255, 0.9)',
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
  },
  iconsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: responsive(10, 12, 16, 20),
  },
  searchButton: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.15)',
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default SearchInput;
